import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@component/dialog";
import { Button } from "@component/button";
import { AlertTriangleIcon, ArchiveIcon, Loader2Icon } from "lucide-react";
import type { AdminSubscriptionPackage } from "@service/admin-subscription-packages";

interface Props {
    isOpen: boolean;
    pkg: AdminSubscriptionPackage | null;
    // True while the archive request is in flight — disables both buttons and
    // shows a spinner on the confirm action.
    archiving: boolean;
    onClose: () => void;
    onConfirm: (pkg: AdminSubscriptionPackage) => void;
}

// House destructive-confirm dialog (mirrors DeleteJobConfirmationDialog): red
// AlertTriangle bubble + title row, full-width body, destructive confirm button.
// Archive is a reversible soft-archive (re-activate via the edit form), so it
// skips the "type DELETE" friction gate of the permanent-delete dialog — a
// single destructive confirm is the right weight.
export default function ArchivePackageConfirmationDialog({
    isOpen,
    pkg,
    archiving,
    onClose,
    onConfirm,
}: Props) {
    const handleConfirm = () => {
        if (pkg && !archiving) onConfirm(pkg);
    };

    return (
        <Dialog
            open={isOpen}
            // Don't let an outside-click / Esc dismiss mid-request.
            onOpenChange={(open) => {
                if (!open && !archiving) onClose();
            }}
        >
            <DialogContent className="sm:max-w-[480px] gap-4">
                <DialogHeader className="space-y-3">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/20 flex items-center justify-center flex-shrink-0">
                            <AlertTriangleIcon className="h-5 w-5 text-red-600 dark:text-red-500" />
                        </div>
                        <DialogTitle className="text-lg font-semibold">
                            Archive this package?
                        </DialogTitle>
                    </div>
                </DialogHeader>

                <p className="text-sm text-muted-foreground -mt-2">
                    <span className="font-medium text-foreground">
                        {pkg?.packageName}
                    </span>{" "}
                    will be archived and hidden from the public plan picker. Existing
                    subscriptions on this package are unaffected, and you can
                    re-activate it any time by editing the package.
                </p>

                <div className="flex gap-2 mt-2">
                    <Button
                        onClick={onClose}
                        variant="outline"
                        className="flex-1"
                        disabled={archiving}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirm}
                        variant="destructive"
                        className="flex-1"
                        disabled={archiving}
                    >
                        {archiving ? (
                            <>
                                <Loader2Icon className="h-4 w-4 mr-2 animate-spin" />
                                Archiving…
                            </>
                        ) : (
                            <>
                                <ArchiveIcon className="h-4 w-4 mr-2" />
                                Archive package
                            </>
                        )}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
